import React, { useMemo, useRef, useState } from "react";
import { Stack, Card, Tooltip, Zoom } from "@mui/material";
import { Link as LinkRouter, useLocation } from "react-router-dom";
import { EFMEColors } from "../../components/shared";
import QuizIcon from "@mui/icons-material/Quiz";

/**
 * Props type for the AppBar component.
 */
type AppBarProps = {
    /**
     * Additional class names to apply to the AppBar.
     */
    className?: string;

    /**
     * Spacing between icons on the right side of the app bar (in pixels).
     */
    iconSpacing?: number;

    /**
     * Spacing between buttons on the left side of the app bar (in pixels).
     */
    buttonSpacing?: number;

    /**
     * Size of icons on the right side of the app bar (in rem).
     */
    iconSizeXRem?: string;

    /**
     * Size of icons on the left side of the app bar (in rem).
     */
    iconSizeYRem?: string;

    /**
     * Additional MUI-specific styles to apply to the AppBar.
     */
    sx?: any;
};

type AppBarRoute = {
    to: string;
    slug: string;
    docs: string;
};

const appBarRoutes: Array<AppBarRoute> = [
    {
        to: "/matter/management",
        slug: "Manage",
        docs: "https://docs-brown-iota.vercel.app/workflows/management-workflows/",
    },
    {
        to: "/matter/create",
        slug: "Create",
        docs: "https://docs-brown-iota.vercel.app/workflows/creation-workflow/",
    },
    {
        to: "/matter/archived",
        slug: "Archived",
        docs: "https://docs-brown-iota.vercel.app/workflows/management-workflows/",
    },
    {
        to: "/matter/import",
        slug: "Import",
        docs: "https://docs-brown-iota.vercel.app/workflows/import-workflow/",
    },
    {
        to: "matter/merged",
        slug: "Merged",
        docs: "https://docs-brown-iota.vercel.app/workflows/merged-workflows/",
    },
];

const AppBar = (params: AppBarProps) => {
    const [final_sx, _] = useState({
        ...(params.sx ?? {}),
        width: params.sx.width ?? "100%",
        paddingY: params.sx.paddingY ?? 1,
        paddingX: params.sx.paddingX ?? 2,
    });
    const iconSizeXRem = (params.iconSizeXRem ?? 2.5) + "rem";
    const iconSizeYRem = (params.iconSizeYRem ?? 2.5) + "rem";

    function filterRoutes(value: AppBarRoute): AppBarRoute | undefined {
        if (location.pathname !== value.to) {
            return value;
        }
    }

    const findCurrentRoute = (
        value: AppBarRoute,
        index: number,
        obj: AppBarRoute[]
    ): AppBarRoute | undefined => {
        if (location.pathname === value.to) {
            return value;
        }
    };

    function renderLink(value: AppBarRoute): JSX.Element {
        return (
            <LinkRouter
                to={value.to}
                type="button"
                className="btn btn-primary float-right mr-3"
            >
                {value.slug}
            </LinkRouter>
        );
    }
    const location = useLocation();
    const render = useMemo(() => {
        console.log({ ...final_sx });
        return appBarRoutes.filter(filterRoutes).map<JSX.Element>(renderLink);
    }, [location]);

    const linkToDocs = useRef(null);
    const onHoverLinkToDocs: React.MouseEventHandler<HTMLAnchorElement> = (
        e
    ) => {
        const element = e.currentTarget;
        element.style.color = EFMEColors.blue;
    };

    const onHoverLinkToDocsLeave: React.MouseEventHandler<HTMLAnchorElement> = (
        e
    ) => {
        const element = e.currentTarget;
        element.style.color = EFMEColors.darkBlue;
    };

    return (
        <Card sx={{ ...final_sx }}>
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={params.buttonSpacing}>
                    {render}
                </Stack>
                <Stack direction="row" spacing={params.iconSpacing}>
                    <Tooltip
                        title="Click Here For Help About This Page."
                        slots={{
                            transition: Zoom,
                        }}
                    >
                        <a
                            href={appBarRoutes.find(findCurrentRoute)?.docs}
                            target="_blank"
                            rel="noopener noreferrer"
                            id="link-to-docs"
                            ref={linkToDocs}
                            style={{
                                color: EFMEColors.darkBlue,
                                transitionDuration: "100ms",
                            }}
                            onMouseOver={onHoverLinkToDocs}
                            onMouseLeave={onHoverLinkToDocsLeave}
                        >
                            <QuizIcon fontSize="large" />
                        </a>
                    </Tooltip>
                </Stack>
            </Stack>
        </Card>
    );
};

export default AppBar;
